<template>
    <div class="col-lg-12 mx-2">
        <ContentContainer>
            <template v-slot:content>
                <!-- Dashboard Heading -->
                <div class="dashboard-heading mb-4">
                    <div class="dh-icon-wrap dh-icon-setup">
                        <i class="material-icons">domain_add</i>
                    </div>
                    <div>
                        <h5 class="dh-title">Setup Organization</h5>
                        <p class="dh-subtitle">Create a new organization or manage your setup and configuration</p>
                    </div>
                </div>

                <OrganizationSummaryCard
                    v-if="canViewOrganizationSummary"
                    :organizations="organizationSummary"
                    :loading="organizationSummaryLoading"
                    @open="openOrganizationSummary"
                />
                <div v-if="showForm && isLocationNotSet && stSessionData.registrationType == 2 && !stSessionData.isSuperAdmin" class="w-full p-2">
                    <form @submit.prevent="handleSubmit">
                        <div class="container">
                            <div class="row">
                                <div class="my-2 col">
                                    <material-input id="name" type="text" label="Organization Name" name="name"
                                        class="w-full" placeholder="Enter organization name" :isRequired="true"
                                        v-model="organization.name" size="lg"
                                        :error="errors.name" />
                                </div>

                                <div class="my-2 col">
                                    <material-input id="email" type="email" label="Email" name="email" class="w-full"
                                        placeholder="Enter email address" :isRequired="true"
                                        v-model="organization.email" size="lg" :error="errors.email"
                                        @blur="validateEmailField" />
                                </div>
                            </div>

                            <div class="row">
                                <div class="my-2 col">
                                    <material-input id="phone" type="number" label="Phone Number" name="phone"
                                        placeholder="Enter phone number" :isRequired="true"
                                        v-model="organization.phone" :error="errors.phone" />
                                </div>
                                <div class="my-2 col">
                                    <material-input id="description" type="text" label="Description" name="description"
                                        placeholder="Enter description" :isRequired="true"
                                        v-model="organization.description" :error="errors.description" />
                                </div>
                            </div>

                            <div class="row">
                                <div class="my-2 col">
                                    <material-input id="location" type="text" label="Location Name" name="location"
                                        placeholder="Enter Location Name" :isRequired="true"
                                        v-model="organization.locationname"
                                        :error="errors.locationname" />
                                </div>
                                <div class="my-2 col">
                                    <material-input id="website" type="text" label="Website URL" name="website"
                                        placeholder="Enter Website URL" v-model="organization.website" />
                                </div>
                            </div>

                            <div class="row">
                                <div class="my-2 col">
                                    <MaterialSelect id="country" type="text" label="Country" name="country"
                                        :options="countriesOptions" placeholder="Select Country" :isRequired="true"
                                        v-model="organization.selectedCountry"
                                        :error="errors.selectedCountry" />
                                </div>

                                <div class="my-2 col">
                                    <MaterialSelect id="timezone" type="text" label="TimeZone" name="timezone"
                                        :options="timezonesOptions" placeholder="Select Timezone" :isRequired="true"
                                        v-model="organization.selectedTimezone"
                                        :error="errors.selectedTimezone" />
                                </div>

                            </div>

                            <div class="ms-auto" style="margin-top: 30px; width: 140px;">
                                <material-button class="mb-2 py-2 d-flex justify-content-evenly align-items-center"
                                    size="xxl" variant="gradient" color="success" fullWidth>
                                    <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960"
                                        width="20px" fill="#EFEFEF">
                                        <path
                                            d="M840-683v503q0 24-18 42t-42 18H180q-24 0-42-18t-18-42v-600q0-24 18-42t42-18h503l157 157Zm-60 27L656-780H180v600h600v-476ZM479.76-245q43.24 0 73.74-30.26 30.5-30.27 30.5-73.5 0-43.24-30.26-73.74-30.27-30.5-73.5-30.5-43.24 0-73.74 30.26-30.5 30.27-30.5 73.5 0 43.24 30.26 73.74 30.27 30.5 73.5 30.5ZM233-584h358v-143H233v143Zm-53-72v476-600 124Z" />
                                    </svg>
                                    Submit
                                </material-button>
                            </div>
                        </div>
                    </form>
                </div>
                <div v-else class="row d-flex m-auto">
                    <div v-for="item in visibleSetupCards" :key="item.id || item.label" class="col-lg-3 col-md-6 col-12">
                        <TabCard 
                            :text="item.label" 
                            :icon="getCardIcon(item.label)"
                            :highlight="noActivities && (item.id === 'setupWizard' || item.label === 'Setup Wizard')"
                            @click="openModal(item)" 
                        />
                    </div>
                </div>

                <Modal v-if="isModalOpen" :modalHeading="modalHeading" :modalContent="modalContent"
                    :iframeUrl="iframeUrl" refresh-on-close @close="closeModal">
                </Modal>

                <!-- Audit Events Card - shows location-level for location admins, org-level for org admins -->
                <AuditEventsCard :scope="stSessionData?.isSuperAdmin ? 'all' : (stSessionData?.isOrganizationAdmin ? 'organization' : 'location')" />

            </template>
        </ContentContainer>
    </div>

    <OrganizationSummaryModal
        v-model="isOrganizationSummaryOpen"
        :organizations="organizationSummary"
        :loading="organizationSummaryLoading"
        :error="organizationSummaryError"
        @retry="loadOrganizationSummary"
    />
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toast-notification';
import Modal from '@/components/Modal/Modal.vue';
import TabCard from '@/components/Cards/TabCards.vue';
import ContentContainer from '@/components/PageLayout/ContentContainer.vue';
import MaterialInput from "@/components/common/MaterialInput.vue";
import MaterialButton from "@/components/common/MaterialButton.vue";
import MaterialSelect from "@/components/common/MaterialSelect.vue";
import { getLocationId, getOrganizationId } from '../utils/SessionDataHandler';
import { processUriPlaceholders } from '../utils/UriHandler';
import { isValidEmail } from '@/utils/validation';
import { baseUrl } from '@/utils/BaseUrl';
import LoggerService from "@/services/LoggerService";
import { usePermissions } from '@/composables/usePermissions';
import api from '@/config/api';
import AuditEventsCard from '@/components/Cards/AuditEventsCard.vue';
import OrganizationSummaryCard from '@/components/OrganizationSummary/OrganizationSummaryCard.vue';
import OrganizationSummaryModal from '@/components/OrganizationSummary/OrganizationSummaryModal.vue';

const store = useStore();
const router = useRouter();
const toast = useToast();
const {
    canAccessSetupWizard,
    isOrganizationAdmin: hasOrganizationAdminPermission,
    isSuperAdmin: hasSuperAdminPermission,
} = usePermissions();

// Helper functions (defined first)
function getInitialOrganization() {
    return {
        name: "",
        email: "",
        phone: "",
        description: "",
        locationname: "main campus",
        selectedCountry: null,
        selectedTimezone: null,
        website: "",
        // createdby: ""
    };
}

function getInitialErrors() {
    return {
        name: null,
        email: null,
        phone: null,
        description: null,
        locationname: null,
        selectedCountry: null,
        selectedTimezone: null
    };
}

// Reactive state
const organization = ref({});
const errors = ref(getInitialErrors());
const isModalOpen = ref(false);
const modalHeading = ref("");
const modalContent = ref("");
const iframeUrl = ref("");
const showForm = ref(true);
const noActivities = ref(false);
const isOrganizationSummaryOpen = ref(false);

// Reactive location and organization IDs that update when session changes
const locationId = computed(() => stSessionData.value?.locationid || getLocationId());
const organizationId = computed(() => stSessionData.value?.organizationid || getOrganizationId());

// Get setup cards from store and make URIs dynamic
const stSetupOrgCards = computed(() => store.getters.stSetupOrgCards);

// Static billing card definition
const billingCard = {
    id: "billing",
    label: "Billing",
    uri: `${baseUrl}/MF/cs-mf-billing/?locationid=<locationid>`,
    uri_type: "modal"
};

// Quick Setup card definition — navigates via router, not iframe
const quickSetupCard = {
    id: "quickSetup",
    label: "Quick Setup (AI)",
    uri: "/quick-setup",
    uri_type: "page"
};

// Setup cards with dynamic URIs
const setupCards = computed(() => {
    let cards = stSetupOrgCards.value || [];
    
    // Add Quick Setup card at the beginning if not already present
    if (!cards.some(card => card.id === "quickSetup" || card.label === "Quick Setup (AI)")) {
        cards = [quickSetupCard, ...cards];
    }

    // Add billing card after "Edit Location" if not already present
    if (!cards.some(card => card.id === "billing")) {
        const editLocationIndex = cards.findIndex(card => card.id === "editLocation" || card.label === "Edit Location");
        if (editLocationIndex !== -1) {
            cards = [
                ...cards.slice(0, editLocationIndex + 1),
                billingCard,
                ...cards.slice(editLocationIndex + 1)
            ];
        } else {
            cards = [...cards, billingCard];
        }
    }

    if (cards.length === 0) {
        return [];
    }

    return processUriPlaceholders(cards, {
        locationId: locationId.value,
        organizationId: organizationId.value
    });
});

const stPermissionData = computed(() => store.getters.stPermissionData);

// Cards injected client-side that should bypass backend permission filtering
const clientInjectedCardIds = ['quickSetup', 'billing'];

const visibleSetupCards = computed(() => {
    const allowed = stPermissionData.value?.setupOrgCards;
    let cards = setupCards.value;
    if (allowed?.length) {
        cards = cards.filter((card) =>
            clientInjectedCardIds.includes(card?.id) || allowed.includes(card?.id || card?.label)
        );
    }
    // Setup Wizard card requires Location/Organization/SuperAdmin
    if (!canAccessSetupWizard.value) {
        cards = cards.filter((card) => card?.id !== 'setupWizard' && card?.label !== 'Setup Wizard');
    }
    return cards;
});

// Map each setup card to different icon and color
function getCardIcon(label) {
    const iconMap = {
        "Quick Setup (AI)": {
            name: "auto_awesome",
            color: "text-white",
            background: "info"
        },
        "Setup Wizard": {
            name: "settings",
            color: "text-white",
            background: "primary"
        },
        "Edit Organization": {
            name: "business",
            color: "text-white",
            background: "info"
        },
        "Edit Location": {
            name: "location_on",
            color: "text-white",
            background: "success"
        },
        "Billing": {
            name: "credit_card",
            color: "text-white",
            background: "warning"
        },
        "Setup New Organization": {
            name: "add_business",
            color: "text-white",
            background: "primary"
        },
        "Join Organization": {
            name: "group_add",
            color: "text-white",
            background: "success"
        }
    };
    
    return iconMap[label] || {
        name: "weekend",
        color: "text-white",
        background: "dark"
    };
}

// Vuex getters
const stAddOrganizationResponse = computed(() => store.getters.stAddOrganizationResponse);
const stSessionData = computed(() => store.getters.stSessionData);

// Countries and timezones options from store (same shape as EditOrganization)
const countriesOptions = computed(() => {
    const countries = store.getters["stCountries"] || {};
    return Object.keys(countries).map((key) => ({
        label: countries[key],
        value: key
    }));
});

const timezonesOptions = computed(() => {
    const zones = store.getters["stTimezones"] || {};
    return Object.keys(zones).map((key) => ({
        label: zones[key],
        value: key
    }));
});
const stLocations = computed(() => store.getters.stLocations);
const stQuoteData = computed(() => store.getters.stQuoteData);
const organizationSummary = computed(() => store.getters.organizationSummary || []);
const organizationSummaryLoading = computed(() => store.getters.organizationSummaryLoading);
const organizationSummaryError = computed(() => store.getters.organizationSummaryError || '');

const canViewOrganizationSummary = computed(() => {
    const session = stSessionData.value || {};
    const sessionDetails = session.sessionDetails || session.session_details || {};
    const permissionData = store.getters.stFullPermissionData || {};
    return Boolean(
        hasSuperAdminPermission.value
        || hasOrganizationAdminPermission.value
        || session.isSuperAdmin
        || session.is_super_admin
        || sessionDetails.isSuperAdmin
        || sessionDetails.is_super_admin
        || session.isOrganizationAdmin
        || session.organizationadmin
        || session.isAdminInOrganization
        || sessionDetails.isOrganizationAdmin
        || sessionDetails.organizationadmin
        || sessionDetails.isAdminInOrganization
        || ['superadmin', 'organization'].includes(permissionData.admin_level)
    );
});

// Computed properties
const isLocationNotSet = computed(() => {
    return !stSessionData.value.locationid;
});

// Vuex actions
const addOrganization = (payload) => store.dispatch('addOrganization', payload);
const getCountries = () => store.dispatch('getCountries');
const getTimezones = () => store.dispatch('getTimezones');
const setPersonLocations = (locationId) => store.dispatch('setPersonLocations', locationId);
const fetchSessionData = () => store.dispatch('fetchSessionData');
const fetchPersonLocations = (personId) => store.dispatch('fetchPersonLocations', { personid: personId });
const fetchInspirationalQuote = (locationId) => store.dispatch('fetchInspirationalQuote', locationId);
const setQuote = (quote) => store.dispatch('setQuote', quote);
const loadOrganizationSummary = async () => {
    if (!canViewOrganizationSummary.value) return;
    try {
        await store.dispatch('fetchOrganizationSummary');
    } catch (error) {
        console.warn('Could not load organization summary:', error);
    }
};

watch(canViewOrganizationSummary, (canView) => {
    if (canView) {
        loadOrganizationSummary();
    }
}, { immediate: true });

// Vuex mutations
const setLocations = (locations) => store.commit('setLocations', locations);

// Methods
function validateEmailField() {
    if (organization.value.email && organization.value.email.trim() !== '') {
        if (!isValidEmail(organization.value.email)) {
            errors.value.email = 'Please enter a valid email address';
        } else {
            errors.value.email = null;
        }
    }
}

function validateForm() {
    errors.value = getInitialErrors();

    let valid = true;

    if (!organization.value.name) {
        errors.value.name = "Organization Name is required.";
        valid = false;
    }

    if (!organization.value.email) {
        errors.value.email = "Email is required.";
        valid = false;
    } else if (!isValidEmail(organization.value.email)) {
        errors.value.email = "Invalid email format.";
        valid = false;
    }

    if (!organization.value.phone) {
        errors.value.phone = "Phone Number is required.";
        valid = false;
    }

    if (!organization.value.description) {
        errors.value.description = "Description is required.";
        valid = false;
    }

    if (!organization.value.locationname) {
        errors.value.locationname = "Location is required.";
        valid = false;
    }

    if (!organization.value.selectedCountry || organization.value.selectedCountry === '') {
        errors.value.selectedCountry = "Please select a country.";
        valid = false;
    }

    if (!organization.value.selectedTimezone || organization.value.selectedTimezone === '') {
        errors.value.selectedTimezone = "Please select a timezone.";
        valid = false;
    }

    return valid;
}

async function handleSubmit() {
    if (!validateForm()) {
        toast.error("Please correct the errors before submitting.", {
            position: 'top-right',
        });
        return;
    }

    const payload = {
        ...organization.value,
        createdby: stSessionData.value.personId,
        // Use org email/phone for the location as well
        locationemail: organization.value.email,
        locationphone: organization.value.phone,
    };

    try {
        await addOrganization(payload);

        const orgResponse = stAddOrganizationResponse.value;
        const newOrgId = orgResponse.organizationid;
        const newLocationId = orgResponse.locationid;

        // Log new organization creation
        LoggerService.logDataOperation(
            'CREATE',
            'organization',
            newOrgId,
            {
                organizationName: organization.value.name,
                locationName: organization.value.locationname,
                country: organization.value.selectedCountry,
                timezone: organization.value.selectedTimezone
            },
            stSessionData.value?.personId
        );
        
        // Log new location creation
        LoggerService.logDataOperation(
            'CREATE',
            'location',
            newLocationId,
            {
                locationName: organization.value.locationname,
                organizationId: newOrgId
            },
            stSessionData.value?.personId
        );

        toast.success(orgResponse.message, {
            position: 'top-right',
        });

        await setPersonLocations(newLocationId);

        // Clear stale session cache so fetchSessionData hits the API
        // and picks up the newly created locationid
        localStorage.removeItem('apiCache_getsessiondetails');
        await fetchSessionData();

        await fetchPersonLocations(stSessionData.value.personId);
        setLocations(stLocations.value);

        await fetchInspirationalQuote(newLocationId);
        setQuote(stQuoteData.value);

        showForm.value = false;
        // Navigate to Quick Setup instead of reloading (which would open the old wizard)
        router.push('/quick-setup');
    } catch (error) {
        console.error('Error submitting organization:', error);
        toast.error("Failed to submit organization. Please try again.", {
            position: 'top-right',
        });
    }
}

function openModal(item) {
    if (item.uri_type === 'modal') {
        // Open modal with iframe
        isModalOpen.value = true;
        modalHeading.value = item.label;
        // Replace placeholders in URI if needed
        let uri = item.uri;
        if (stSessionData.value.locationid) {
            uri = uri.replace(':locationID', stSessionData.value.locationid);
            uri = uri.replace('<locationid>', stSessionData.value.locationid);
        }
        if (stSessionData.value.organizationid) {
            uri = uri.replace(':organizationID', stSessionData.value.organizationid);
            uri = uri.replace('<organizationid>', stSessionData.value.organizationid);
        }
        iframeUrl.value = uri;
    } else if (item.uri_type === 'page') {
        // Navigate to page using router
        let routePath = item.uri;
        // Replace placeholders in route path
        if (stSessionData.value.locationid) {
            routePath = routePath.replace(':locationID', stSessionData.value.locationid);
            routePath = routePath.replace('<locationid>', stSessionData.value.locationid);
        }
        if (stSessionData.value.organizationid) {
            routePath = routePath.replace(':organizationID', stSessionData.value.organizationid);
            routePath = routePath.replace('<organizationid>', stSessionData.value.organizationid);
        }
        router.push(routePath);
    }
}

function closeModal() {
    isModalOpen.value = false;
}

function openOrganizationSummary() {
    isOrganizationSummaryOpen.value = true;
    if (!organizationSummary.value.length && !organizationSummaryLoading.value) {
        loadOrganizationSummary();
    }
}

// Watch for session data changes to update organizationid when location changes
// This follows the pattern from Dropdown.vue
watch(
    () => stSessionData.value?.locationid,
    (newLocationId) => {
        if (newLocationId && stLocations.value) {
            // Locations structure matches Dropdown.vue: array of organizations with nested locations
            const locationList = Array.isArray(stLocations.value) ? stLocations.value : [stLocations.value];

            // Find the organization that contains this location
            const foundOrg = locationList.find(org =>
                org.locations?.some(loc => String(loc.location_id) === String(newLocationId))
            );

            if (foundOrg && foundOrg.organization_id) {
                // Update session data with organizationid
                const sessionData = {
                    ...stSessionData.value,
                    organizationid: foundOrg.organization_id
                };
                store.commit("SET_SESSION_DATA", sessionData);
            }
        }
    },
    { immediate: true }
);

// Also watch locations data to update organizationid when locations are loaded
watch(
    () => stLocations.value,
    (newLocations) => {
        const currentLocationId = stSessionData.value?.locationid;
        if (currentLocationId && newLocations) {
            const locationList = Array.isArray(newLocations) ? newLocations : [newLocations];

            const foundOrg = locationList.find(org =>
                org.locations?.some(loc => String(loc.location_id) === String(currentLocationId))
            );

            if (foundOrg && foundOrg.organization_id) {
                const sessionData = {
                    ...stSessionData.value,
                    organizationid: foundOrg.organization_id
                };
                store.commit("SET_SESSION_DATA", sessionData);
            }
        }
    },
    { deep: true }
);

// Auto-open Setup Wizard if location exists but no activities
async function checkAndOpenSetupWizard() {
    const locId = locationId.value;
    if (!locId) return;

    try {
        // Fetch activities for this location (minimal page to check existence)
        const response = await api.get(
            `/activities/location/${locId}?page_size=1&page_number=1`
        );
        const activities = response.data?.data || [];
        const totalRecords = response.data?.pagination?.total_records ?? activities.length;

        if (totalRecords === 0) {
            noActivities.value = true;
            // Organization and super admins need to see the organization
            // command-center card even before their first activity exists.
            // Location-only admins retain the existing quick-setup redirect.
            if (!canViewOrganizationSummary.value) {
                router.push('/quick-setup');
            }
        } else {
            noActivities.value = false;
        }
    } catch (error) {
        console.warn('Could not check activities for auto-opening Setup Wizard:', error);
    }
}

// Lifecycle hooks
onMounted(() => {
    if (isLocationNotSet.value && stSessionData.value.registrationType == 2 && !stSessionData.value.isSuperAdmin) {
        getCountries();
        getTimezones();
        organization.value = getInitialOrganization();
        
        // Pre-select country and timezone from user's session data if available
        if (stSessionData.value.country) {
            organization.value.selectedCountry = stSessionData.value.country;
        }
        if (stSessionData.value.time_zone) {
            organization.value.selectedTimezone = stSessionData.value.time_zone;
        }
    } else if (!isLocationNotSet.value) {
        // Location exists — check if Setup Wizard should auto-open
        checkAndOpenSetupWizard();
    }
});
</script>

<style scoped>
/* Dashboard heading */
.dashboard-heading {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem 1.25rem;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.dh-icon-wrap {
    width: 52px;
    height: 52px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #fff;
}
.dh-icon-wrap i { font-size: 28px; }
.dh-icon-setup { background: linear-gradient(135deg, #7e57c2, #4527a0); }
.dh-title {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 700;
    color: #344767;
}
.dh-subtitle {
    margin: 0;
    font-size: 0.78rem;
    color: #7b809a;
    margin-top: 2px;
}
</style>
